import type { ComponentProps, FC, ReactNode } from 'react';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { getRequestIdentifier } from 'relay-runtime';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { AudioContentProvider } from '@confluence/audio/entry-points/AudioContentProvider.tsx';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import {
	SPACE_OVERVIEW_EXPERIENCE,
	VIEW_PAGE_EXPERIENCE,
	VIEW_ALL_MACRO_EXPERIENCE,
	FRONT_COVER_LOAD_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { routeNameToContentTypeMap } from '@confluence/experience-tracker/entry-points/startExperienceForRoute.ts';
import {
	getExperienceTracker,
	stopExperienceOnError,
} from '@confluence/experience-tracker/entry-points/getExperienceTracker.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { usePageState } from '@confluence/page-context';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { ContentContainer } from '@confluence/content-container/entry-points/ContentContainer.ts';
import {
	ContentBlogUnifiedQueryArtifact,
	ContentUnifiedQueryArtifact,
	createContentBlogUnifiedQueryVariables,
	createContentUnifiedQueryVariables,
	type ContentBlogUnifiedQueryType,
	type ContentBlogUnifiedQueryVariablesType,
	type ContentUnifiedQueryType,
	type ContentUnifiedQueryVariablesType,
} from '@confluence/content-unified-query';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { isExpectedApolloError } from '@confluence/error-boundary/entry-points/expectedGraphQlErrorHelper.ts';
import { getProcessedCommentsDataState } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { expVal, expValEquals, isExperimentEnabled } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { PagePrerequisites } from '@confluence/content-prerequisites/entry-points/PagePrerequisites.ts';
import { useDocumentUpdateStatusDispatch } from '@confluence/annotation-provider-store/entry-points/DocumentUpdateStatusContext.ts';
import {
	VIEW_PAGE,
	SPACE_OVERVIEW,
	COMPANY_HUB,
} from '@confluence/named-routes/entry-points/routes.ts';
import { RENDERER } from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceStart } from '@confluence/macro-tracker/entry-points/MacroExperienceStart.ts';
import { ContentStateContainer } from '@confluence/content-state/entry-points/useContentState.ts';
import {
	ReloadType,
	ReloadPriority,
	useQuickReloadSubscriber,
} from '@confluence/quick-reload/entry-points/subscription.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getAnalyticsBylineGateReason } from '@confluence/confluence-analytics/entry-points/analyticsBylineEligibility.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { useUnreadCommentsSetup } from '@confluence/unread-comments/entry-points/useUnreadCommentsSetup.ts';
import { getAutohighlightsQueryVariables } from '@confluence/contextual-reading-aids/entry-points/getAutohighlightsQueryVariables.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { useIsTransition } from '@confluence/route-manager/entry-points/RouteState.ts';
import {
	RuntimeDocumentQuery,
	DocumentQueryCancelledError,
	type DocumentQueryResult,
} from './RuntimeDocumentQuery.tsx';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';

import type { BaseViewPageProps } from './BaseViewPageProps.ts';
import { VIEW_PAGE_CONTAINER_METRIC } from './perf.config.tsx';
import { useContentExtensionMetrics } from './useContentExtensionMetrics.ts';
import { useContentReloadOnFabricSupportChange } from './useContentReloadOnFabricSupportChange.ts';
import { getViewPageContentError } from './getViewPageContentError.ts';

const pageMap = {
	SPACE_OVERVIEW: {
		route: SPACE_OVERVIEW,
		errorAttribution: 'next-space-overview',
		name: SPACE_OVERVIEW_EXPERIENCE,
		isContentView: false,
	},
	COMPANY_HUB: {
		route: COMPANY_HUB,
		errorAttribution: 'next-company-hub',
		name: FRONT_COVER_LOAD_EXPERIENCE,
		isContentView: false,
	},
	page: {
		route: VIEW_PAGE,
		errorAttribution: 'next-view-page',
		name: VIEW_PAGE_EXPERIENCE,
		isContentView: true,
	},
};

type ViewPageContentQueryData = ContentUnifiedQueryType | ContentBlogUnifiedQueryType | undefined;

export type ViewPageHeaderRenderer = (contentQueryData: ViewPageContentQueryData) => ReactNode;

interface ViewPageContainerProps extends BaseViewPageProps {
	children?: (props: {
		isThemed: boolean;
		contentQueryData: ViewPageContentQueryData;
		contentQueryError: ApolloErrorInterop | undefined;
		contentQueryHasRawError: boolean;
		contentQueryRefetch: () => Promise<void>;
	}) => React.ReactNode | null;
	stickyHeader?: ReactNode | ViewPageHeaderRenderer;
	contentScreenStyles?: React.CSSProperties;
	isContainerContentView?: boolean;
	versionOverride?: number | null;
	customPerfData?: Record<string, string | boolean | object>;
	isEmbeddedPage?: boolean;
	hasDocumentContractAnalyticsByline?: boolean;
	hasDocumentContractContentHeader?: boolean;
}

export const ViewPageContainer: FC<ViewPageContainerProps> = memo(
	({
		contentId,
		spaceKey,
		children,
		stickyHeader,
		contentScreenStyles,
		isContainerContentView = true,
		versionOverride = null,
		customPerfData,
		isEmbeddedPage,
		hasDocumentContractAnalyticsByline = false,
		hasDocumentContractContentHeader = false,
	}) => {
		const [{ routeName = '', contentType }] = usePageState();
		const isTransition = useIsTransition();
		const {
			activationId,
			accessStatus,
			cloudId,
			edition,
			isAdminHubAIEnabled,
			isLicensed,
			isLoggedIn,
			isRovoLLMEnabled,
			userId,
		} = useSessionData();
		const hasAnalyticsBylineGateReason = Boolean(
			getAnalyticsBylineGateReason({ edition, isLoggedIn, accessStatus, userId }),
		);
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		const isSSR = Boolean(process.env.REACT_SSR || window.__SSR_RENDERED__);

		useUnreadCommentsSetup({
			pageId: contentId,
			pageContentTypeProps: {
				pageMode: 'view',
			},
		});

		const isBlogpost = contentType === 'blogpost';
		const attachmentDownloadDspAction = 'ATTACHMENT_DOWNLOAD';
		const shouldUseRouteOwnedDsp =
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			routeName !== SPACE_OVERVIEW.name &&
			routeName !== COMPANY_HUB.name &&
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			!StatsigConfigurations.isKillSwitchOn('confluence_vp_dsp_reader') &&
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			expVal('confluence_exp_vp_document', 'isEnabled', false);
		const dspAction = shouldUseRouteOwnedDsp ? attachmentDownloadDspAction : undefined;
		const shouldUseRouteOwnedCustomChrome =
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			routeName !== SPACE_OVERVIEW.name &&
			routeName !== COMPANY_HUB.name &&
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			!StatsigConfigurations.isKillSwitchOn('confluence_vp_custom_chrome_reader') &&
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			expVal('confluence_exp_vp_document', 'isEnabled', false);
		const shouldUseContentHeaderFragment =
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			hasDocumentContractContentHeader &&
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			!StatsigConfigurations.isKillSwitchOn('confluence_vp_header_frag_reader') &&
			expVal('confluence_exp_vp_document', 'isEnabled', false);
		const shouldUseFabricEligibilityFragment =
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			shouldUseContentHeaderFragment &&
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			!StatsigConfigurations.isKillSwitchOn('confluence_vp_fabric_eligibility_frag_reader') &&
			expVal('confluence_exp_vp_document', 'isEnabled', false);

		const shouldUseRouteOwnedMedia =
			!StatsigConfigurations.isKillSwitchOn('confluence_vp_media_reader') &&
			expVal('confluence_exp_vp_document', 'isEnabled', false) &&
			!expVal('confluence_media_token_preload_agg_query', 'isEnabled', false);
		const skipContentQuery = !contentId || !spaceKey;
		const autohighlightsQueryVariables =
			userId &&
			!StatsigConfigurations.isKillSwitchOn('confluence_vp_autohighlights_unified_query') &&
			expVal('confluence_exp_vp_document', 'isEnabled', false)
				? getAutohighlightsQueryVariables({
						contentId,
						cloudId,
						activationId,
						contentType: isBlogpost ? 'blogpost' : 'page',
					})
				: undefined;
		// Keep the hydrated query aligned with useAIRovoButtonEnablement without coupling this
		// content-data boundary to the Rovo Button hook.
		const isRovoButtonEnabled = isAdminHubAIEnabled && isRovoLLMEnabled;
		const rovoActivationQueryVariables =
			!isBlogpost &&
			routeName !== SPACE_OVERVIEW.name &&
			routeName !== COMPANY_HUB.name &&
			isLoggedIn &&
			isLicensed &&
			userId &&
			cloudId &&
			isRovoButtonEnabled &&
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			fg('confluence_rovo_agent_session_nudges')
				? { cloudId }
				: undefined;

		const pageContentQueryVariables = {
			...createContentUnifiedQueryVariables({
				userId,
				cloudId: isLoggedIn ? cloudId : undefined,
				contentId,
				spaceKey,
				versionOverride,
				isSSR,
				// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
				includeRendererAssetsMedia: shouldUseRouteOwnedMedia,
				// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
				includeRendererAssetsCustomChrome: shouldUseRouteOwnedCustomChrome,
				includeRendererAssetsDsp: Boolean(dspAction),
				rendererAssetsDspAction: dspAction,
				// confluence-feature-gating/inline-usage: expValEquals must be inlined
				agentSessionsUserId:
					!!userId && expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true)
						? userId
						: undefined,
				// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
				includeAgentTrustReceiptData: fg('cc_sparks_agent_trust_receipt'),
				includeBylineContributors:
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_title_byline_frag_dedup') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				includeConnectionAncestors: isExperimentEnabled(
					'confluence_1p_and_3p_connection_byline_experiment',
				),
				// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
				includeAutomationDiscoveries: fg('cc_automations_discovery_nudges'),
				includeMetadata:
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_meta_frag_reader') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				includeClassification:
					!StatsigConfigurations.isKillSwitchOn(
						'confluence_vp_classification_context_frag_reader',
					) && expVal('confluence_exp_vp_document', 'isEnabled', false),
				includeReactions:
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_reactions_frag_reader') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				includeAudioContent:
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_audio_frag_reader') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
				includeAnalyticsByline:
					!isBlogpost &&
					hasDocumentContractAnalyticsByline &&
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_analytics_byline_frag_reader') &&
					!hasAnalyticsBylineGateReason &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
				includeContentHeader: shouldUseContentHeaderFragment,
				includeSmartCreateFeedback:
					shouldUseContentHeaderFragment &&
					!versionOverride &&
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_smart_create_banner_frag_reader') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				includeContentHeaderAccess:
					shouldUseContentHeaderFragment &&
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_access_frag_reader') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				includeFabricEditorEligibility: shouldUseFabricEligibilityFragment,
				includeContentHeaderPublicLink:
					shouldUseContentHeaderFragment &&
					!isBlogpost &&
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_access_frag_reader') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				rovoActivationQueryVariables,
				autohighlightsQueryVariables,
			}),
			...(!StatsigConfigurations.isKillSwitchOn('confluence_vp_mode_frag_reader') &&
			expVal('confluence_exp_vp_document', 'isEnabled', false)
				? { useContentModeFragment: true }
				: {}),
		};
		const blogContentQueryVariables = {
			...createContentBlogUnifiedQueryVariables({
				userId,
				cloudId: isLoggedIn ? cloudId : undefined,
				contentId,
				spaceKey,
				versionOverride,
				isSSR,
				// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
				includeRendererAssetsMedia: shouldUseRouteOwnedMedia,
				// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
				includeRendererAssetsCustomChrome: shouldUseRouteOwnedCustomChrome,
				includeRendererAssetsDsp: Boolean(dspAction),
				rendererAssetsDspAction: dspAction,
				// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
				includeAgentTrustReceiptData: fg('cc_sparks_agent_trust_receipt'),
				includeBylineContributors:
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_title_byline_frag_dedup') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
				includeAutomationDiscoveries: fg('cc_automations_discovery_nudges'),
				includeMetadata:
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_meta_frag_reader') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				includeClassification:
					!StatsigConfigurations.isKillSwitchOn(
						'confluence_vp_classification_context_frag_reader',
					) && expVal('confluence_exp_vp_document', 'isEnabled', false),
				includeReactions:
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_reactions_frag_reader') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				includeAudioContent:
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_audio_frag_reader') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
				includeAnalyticsByline:
					isBlogpost &&
					hasDocumentContractAnalyticsByline &&
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_analytics_byline_frag_reader') &&
					!hasAnalyticsBylineGateReason &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
				includeContentHeader: shouldUseContentHeaderFragment,
				includeSmartCreateFeedback:
					shouldUseContentHeaderFragment &&
					!versionOverride &&
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_smart_create_banner_frag_reader') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				includeContentHeaderAccess:
					shouldUseContentHeaderFragment &&
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_access_frag_reader') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				includeFabricEditorEligibility: shouldUseFabricEligibilityFragment,
				includeContentHeaderPublicLink: false,
				autohighlightsQueryVariables,
			}),
			...(!StatsigConfigurations.isKillSwitchOn('confluence_vp_mode_frag_reader') &&
			expVal('confluence_exp_vp_document', 'isEnabled', false)
				? { useContentModeFragment: true }
				: {}),
		};

		const loadMarkers = (
			<>
				<PageSegmentLoadStart key={contentId} metric={VIEW_PAGE_CONTAINER_METRIC} />
				<MacroExperienceStart
					contentId={contentId}
					mode={RENDERER}
					name={VIEW_ALL_MACRO_EXPERIENCE}
					timeout={ExperienceTimeout.MACRO_LOAD}
				/>
			</>
		);
		const rendererProps = {
			contentId,
			spaceKey,
			children,
			stickyHeader,
			contentScreenStyles,
			isContainerContentView,
			versionOverride,
			customPerfData,
			isEmbeddedPage,
			shouldUseRouteOwnedMedia,
		};
		if (
			isTransition &&
			routeName === VIEW_PAGE.name &&
			!isBlogpost &&
			!isEmbeddedPage &&
			!skipContentQuery &&
			!versionOverride &&
			!StatsigConfigurations.getBoolean('confluence_vp_preloaded_query', true) &&
			expVal('confluence_exp_vp_document', 'isEnabled', false)
		) {
			return (
				<>
					{loadMarkers}
					<RuntimeDocumentQuery
						key={getRequestIdentifier(
							ContentUnifiedQueryArtifact.params,
							pageContentQueryVariables,
						)}
						variables={pageContentQueryVariables}
					>
						{(result) => (
							<ViewPageContainerContents {...rendererProps} rawContentQueryResult={result} />
						)}
					</RuntimeDocumentQuery>
				</>
			);
		}
		return (
			<>
				{loadMarkers}
				<LegacyDocumentQuery
					{...rendererProps}
					isBlogpost={isBlogpost}
					pageContentQueryVariables={pageContentQueryVariables}
					blogContentQueryVariables={blogContentQueryVariables}
				/>
			</>
		);
	},
);

type RendererProps = ViewPageContainerProps & { shouldUseRouteOwnedMedia: boolean };

function LegacyDocumentQuery({
	isBlogpost,
	pageContentQueryVariables,
	blogContentQueryVariables,
	...props
}: RendererProps & {
	isBlogpost: boolean;
	pageContentQueryVariables: ContentUnifiedQueryVariablesType;
	blogContentQueryVariables: ContentBlogUnifiedQueryVariablesType;
}) {
	const skipContentQuery = !props.contentId || !props.spaceKey;
	const pageContentQueryResult = useQueryInterop<
		ContentUnifiedQueryType,
		ContentUnifiedQueryVariablesType
	>(ContentUnifiedQueryArtifact, {
		fetchPolicy: 'cache-first',
		errorPolicy: 'all',
		context: { single: true },
		skip: skipContentQuery || isBlogpost,
		variables: pageContentQueryVariables,
	});

	const blogContentQueryResult = useQueryInterop<
		ContentBlogUnifiedQueryType,
		ContentBlogUnifiedQueryVariablesType
	>(ContentBlogUnifiedQueryArtifact, {
		fetchPolicy: 'cache-first',
		errorPolicy: 'all',
		context: { single: true },
		skip: skipContentQuery || !isBlogpost,
		variables: blogContentQueryVariables,
	});

	const rawContentQueryResult = isBlogpost ? blogContentQueryResult : pageContentQueryResult;
	const { refetch: refetchQuery } = rawContentQueryResult;
	const refetch = useCallback(async () => {
		const result = await refetchQuery();
		return { publishedVersion: result.data?.content?.nodes?.[0]?.version?.number };
	}, [refetchQuery]);
	const result = useMemo(
		() => ({ ...rawContentQueryResult, refetch }),
		[rawContentQueryResult, refetch],
	);
	return <ViewPageContainerContents {...props} rawContentQueryResult={result} />;
}

function ViewPageContainerContents({
	contentId,
	spaceKey,
	children,
	stickyHeader,
	contentScreenStyles,
	isContainerContentView = true,
	versionOverride = null,
	customPerfData,
	isEmbeddedPage,
	shouldUseRouteOwnedMedia,
	rawContentQueryResult,
}: RendererProps & { rawContentQueryResult: DocumentQueryResult }) {
	const [{ routeName = '' }] = usePageState();
	const { edition } = useSessionData();
	const { setCurrentDocumentVersion, setPublishedDocumentVersion } =
		useDocumentUpdateStatusDispatch();
	// The preloader's path check does not cover errors returned by this runtime query.
	// Filter before both PagePrerequisites and the renderer receive the shared result.
	const { contentQueryResult, optionalFieldError } = useMemo(() => {
		let optionalFieldError: ApolloErrorInterop | undefined;
		const error = getViewPageContentError(
			rawContentQueryResult.error,
			contentId,
			rawContentQueryResult.data?.content?.nodes?.[0]?.id,
			(optionalError) => {
				optionalFieldError = optionalError;
			},
		);
		return {
			contentQueryResult: { ...rawContentQueryResult, error },
			optionalFieldError,
		};
	}, [rawContentQueryResult, contentId]);

	const { route, errorAttribution, name, isContentView } = pageMap[routeName] ?? pageMap['page'];

	// The space-overview preloader must populate the same ContentUnifiedQuery cache key this
	// cache-first read uses. Report the relevant routing and experiment inputs when it does not,
	// including the route-owned-media variable which can split the two cache entries.
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr -- Records an SSR-only cache miss; it does not alter rendered output.
		process.env.REACT_SSR &&
		routeName === 'SPACE_OVERVIEW' &&
		!contentQueryResult.data?.content?.nodes?.[0]
	) {
		getMonitoringClient().submitError(
			new Error(
				`SpaceOverview SSR ContentUnifiedQuery cache miss; contentId=${contentId}; spaceKey=${spaceKey}; loading=${contentQueryResult.loading}; hasError=${Boolean(contentQueryResult.error)}; includeRendererAssetsMedia=${shouldUseRouteOwnedMedia}; documentExperiment=${expVal('confluence_exp_vp_document', 'isEnabled', false)}; bodyFragmentReaderKillSwitch=${StatsigConfigurations.isKillSwitchOn('confluence_vp_body_frag_reader')}`,
			),
			{ attribution: Attribution.BACKBONE },
		);
	}

	const { refetch } = contentQueryResult;

	useContentExtensionMetrics(
		contentQueryResult?.data?.content?.nodes?.[0]?.body?.dynamic?.value,
		customPerfData,
		contentQueryResult?.data?.content?.nodes?.[0]?.id ?? contentId,
	);

	// Use this instead of `contentQueryResult.data?.content?.nodes?.[0]?.version?.number`
	// because that field is fetched by other queries too, and thus may become mismatched
	// with the actual version of `body` in GQL cache.
	const contentVersion =
		contentQueryResult.data?.content?.nodes?.[0]?.body?.dynamic?.content?.version?.number;

	useEffect(() => {
		if (contentVersion) {
			setCurrentDocumentVersion(contentVersion);
		}
	}, [contentVersion, setCurrentDocumentVersion]);

	const reload = useCallback(
		async () => {
			getExperienceTracker().start({
				name,
				timeout: ExperienceTimeout.PAGE_LOAD,
				attributes: {
					contentType: routeNameToContentTypeMap[routeName],
					contentId,
					isSpaceOverviewNextRendered: true,
					edition,
					quickReload: true, //tell exp metrics error came from reload and not primary experience
				},
			});

			try {
				const refetchContentQueryResult = await refetch();

				if (refetchContentQueryResult.publishedVersion != null) {
					setPublishedDocumentVersion(refetchContentQueryResult.publishedVersion);
				}
			} catch (error) {
				if (error instanceof DocumentQueryCancelledError) {
					getExperienceTracker().abort({ name, reason: 'Document changed during reload' });
					return;
				}
				if (isUnauthorizedError(error) || isExpectedApolloError(error)) {
					markErrorAsHandled(error);
					getExperienceTracker().abort({
						name,
						reason: 'Expected Apollo error',
					});
					return; //if it's an expected GQL failure don't throw or fail the experience
				}
				stopExperienceOnError(name, error);
				throw error; //this successfully avoids the success below
			}
			getExperienceTracker().succeed({ name });
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[refetch, contentId],
	);

	const content = contentQueryResult.data?.content?.nodes?.[0];
	// Downstream packages are checked against the last generated content-unified-query declaration.
	const contentWithAudioFragment = content as typeof content & {
		readonly audioContentFragment?: ComponentProps<
			typeof AudioContentProvider
		>['contentFragmentRef'];
	};
	const fabricEditorSupported = contentQueryResult.loading
		? null
		: (content?.metadata?.frontend?.fabricEditorSupported ?? null);
	const bodyRepresentation = contentQueryResult.loading
		? null
		: (content?.body?.dynamic?.representation ?? null);
	useContentReloadOnFabricSupportChange({
		contentId,
		fabricEditorSupported,
		bodyRepresentation,
		reload,
	});

	useQuickReloadSubscriber({
		name: 'content',
		types: [ReloadType.content, ReloadType.comment],
		reload,
	});

	useQuickReloadSubscriber({
		name: 'commentsDataManager',
		types: [ReloadType.comment],
		reload: async () => {
			await getProcessedCommentsDataState().refetch?.();
		},
		priority: ReloadPriority.background,
	});

	const resolvedStickyHeader =
		typeof stickyHeader === 'function' ? stickyHeader(contentQueryResult.data) : stickyHeader;

	return (
		<>
			{optionalFieldError && <ErrorDisplay error={optionalFieldError} />}
			<PagePrerequisites
				route={route}
				contentQueryResult={contentQueryResult}
				versionOverride={versionOverride}
			>
				<AudioContentProvider
					contentId={contentId}
					contentFragmentRef={contentWithAudioFragment?.audioContentFragment}
				>
					<ContentStateContainer scope={contentId}>
						<ContentContainer
							fetchTheme
							contentThemeSpaceData={
								contentQueryResult.data ? (contentQueryResult.data.space ?? null) : undefined
							}
							contentId={contentId}
							spaceKey={spaceKey}
							key="content-screen"
							errorAttribution={errorAttribution}
							isScreenContentView={isContentView}
							isContainerContentView={isContainerContentView}
							contentScreenStyles={contentScreenStyles}
							stickyHeader={resolvedStickyHeader}
							versionOverride={versionOverride}
							isEmbeddedPage={isEmbeddedPage}
						>
							{({ isThemed }) => (
								<article>
									{children &&
										children({
											isThemed,
											contentQueryData: contentQueryResult.data,
											contentQueryError: contentQueryResult.error,
											contentQueryHasRawError: Boolean(rawContentQueryResult.error),
											contentQueryRefetch: reload,
										})}
									<PageSegmentLoadEnd
										key={contentId}
										metric={VIEW_PAGE_CONTAINER_METRIC}
										customData={{ isThemed }}
									/>
								</article>
							)}
						</ContentContainer>
					</ContentStateContainer>
				</AudioContentProvider>
			</PagePrerequisites>
		</>
	);
}
